"use client";

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  createContext,
  PropsWithChildren,
  useContext,
} from "react";

interface IIntersectionObserverProviderProps {
  callback: IntersectionObserverCallback;
  options?: IntersectionObserverInit;
}

/**
 * Creates a pair of Provider, Consumer for IntersectionObserver that can be used to observe elements in the DOM.
 * This pattern is particularly useful when you want to fire the same callback for multiple elements.
 * Instead of creating a new IntersectionObserver for each element, you can use the instance created by the Provider.
 * @example
 * ```tsx
 * // TrackProductTile.tsx
 * const { usePairedIntersectionObserver, IntersectionObserverProvider } =
 *   createIntersectionObserver();
 * export const TrackProductTileProvider = ({ children }) => {
 *   return (
 *     <IntersectionObserverProvider
 *       callback={() => {
 *         // fire tracking event
 *        }}
 *      >
 *        {children}
 *      </IntersectionObserverProvider>
 *    );
 * };
 *
 * export const useTrackProductTile = () => {
 *   return usePairedIntersectionObserver<HTMLDivElement>();
 * };
 *
 * // SearchPage.tsx
 * const SearchPage = () => {
 *   return (
 *     <TrackProductTileProvider>
 *       // Search Page Content
 *     </TrackProductTileProvider>
 *   )
 * }
 *
 * // ProductTile.tsx
 * const ProductTile = () => {
 *   const elementRef = useTrackProductTile();
 *   return (
 *     <div ref={elementRef}>
 *        // Product Tile Content
 *     </div>
 *   )
 * }
 * ```
 */
export const createIntersectionObserver = () => {
  const Context = createContext<IntersectionObserver | undefined>(undefined);

  const IntersectionObserverProvider: React.FC<
    PropsWithChildren<IIntersectionObserverProviderProps>
  > = ({ callback, options = {}, children }) => {
    const { rootMargin, root, threshold } = options;

    const callbackRef = useRef(callback);
    callbackRef.current = callback;

    const [observer, setObserver] = useState<IntersectionObserver>();

    useEffect(
      () => {
        const observerInstance = new IntersectionObserver(
          (...args) => {
            callbackRef.current(...args);
          },
          { rootMargin, root, threshold }
        );
        setObserver(observerInstance);

        return () => {
          observerInstance?.disconnect();
          setObserver(undefined);
        };
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [rootMargin, root]
    );

    return <Context.Provider value={observer}>{children}</Context.Provider>;
  };

  const usePairedIntersectionObserver = <E extends HTMLElement>() => {
    const observer = useContext(Context);
    const [element, setElement] = useState<E | null | undefined>(null);

    useEffect(() => {
      const elementRef = element;
      if (elementRef) {
        observer?.observe(element);
      }

      return () => {
        if (elementRef) {
          observer?.unobserve(elementRef);
        }
      };
    }, [element, observer]);

    return useCallback((e: E | null | undefined) => {
      setElement(e);
    }, []);
  };

  return { IntersectionObserverProvider, usePairedIntersectionObserver };
};
